import type { CartItem } from "./edit-cart"

export interface PreviewEntry {
  el: HTMLElement
  newText: string
  orig?: string
}

/**
 * The on-page preview entry for a staged cart item, or null when it must NOT be
 * previewed by rewriting the rendered DOM text:
 * - a whole-field Lexical (rich-text) edit carries `fullValue` (a tree) and only
 *   an opaque placeholder in `values` — writing that placeholder into the article
 *   body would replace the whole body with the literal "‹rich body, edited›", so
 *   rich-body edits are excluded and show only on publish. (AHD-7515)
 * - an item with no located DOM node or no edited value has nothing to preview.
 *
 * Carries BOTH `newText` (staged) and `orig` (the true original `old`) so restore
 * can't "restore" to an inline live-edit already written to the node. (AHD-7494)
 */
export function previewEntryFor(
  item: CartItem,
  el: HTMLElement | undefined
): PreviewEntry | null {
  if (item.fullValue) return null
  const v = item.values.en ?? Object.values(item.values)[0]
  return el && v ? { el, newText: v.new, orig: v.old } : null
}

/** Inject staged values into the live DOM; remember originals for restore. */
export function applyPreview(entries: PreviewEntry[]): void {
  for (const e of entries) {
    if (e.orig === undefined) e.orig = e.el.textContent ?? ""
    e.el.textContent = e.newText
    e.el.style.boxShadow = "inset 0 -2px 0 #2b6cff"
  }
}

/** Restore the original text for each previewed node. */
export function restorePreview(entries: PreviewEntry[]): void {
  for (const e of entries) {
    if (e.orig !== undefined) e.el.textContent = e.orig
    e.el.style.removeProperty("box-shadow")
  }
}
